/*================================ STATE & INTERFACE TYPES ==============================*/

import ProfiledDivisionHasUserSerializer from "@/issara-sdk/types/ProfiledDivisionHasUserSerializer_apps_PRX";

export const CARD_NAME = "CardMeGenMain" as const;

/*================================ GENERIC TYPES ==============================*/

export type Days = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";

export type SeriesType = "story2scene" | "script2vdo" | "rss_news";

export type ViewType = "project-detail" | "create-from-script" | "create-from-website" | "generate-video" | "generate-script" | "generate-direct-script";

export type Steps = "load_data" | "process_items" | "generate_assets" | "merge_media" | "prepare_youtube_metadata" | "create_thumbnail" | "upload_to_youtube";

export type SeriesOption = {
  id: number;
  name: string;
};

export type RenderMode = "review" | "final";

export type AssetType = "audio" | "image" | "video"; // --- สำหรับส่ง params /version script2vdo

/*================================ GENERIC STORY2SCENE TYPES ==============================*/

export const STEPS_STORY = [
  { id: "s2s_ai_split_parts", label: "Split Story" },
  { id: "s2s_ai_generate_script", label: "Generate Script" },
  { id: "s2s_ai_character_designer", label: "Design Characters" },
  { id: "s2s_ai_global_style", label: "Define Global Style" },
  { id: "s2s_ai_camera_language", label: "Define Camera Language" },
  { id: "s2s_ai_image_generator", label: "Generate Images" },
  { id: "s2s_merge_script_image", label: "Merge Script & Images" },
  { id: "s2s_save_result_to_json", label: "Check Final Data" },
] as const;

export const STEPS_VDO = [
  { id: "process_items", label: "Process Items (AI)" },
  { id: "generate_assets", label: "Generate Assets" },
  { id: "merge_media", label: "Merge Media (FFmpeg)" },
  { id: "prepare_youtube_metadata", label: "Prepare Metadata" },
  { id: "create_thumbnail", label: "Create Thumbnail" },
  { id: "upload_to_youtube", label: "Upload to YouTube" },
] as const;

/*================================ GENERIC SCRIPT2SCENE TYPES ==============================*/

export const STEPS_SCRIPT2SCENE = [
  { id: "script2scene_ai_script_splitter", label: "Split Script to Scenes" },
  { id: "script2scene_save_result_to_json", label: "Finalize Draft" },
] as const;

/*================================ GENERIC DIRECT SCRIPT TYPES ==============================*/

export const STEPS_DIRECT = [
  { id: "s2v_ai_image_generator", label: "Generate Prompts" },
  { id: "s2v_merge_script_image", label: "Merge Data" }, // TODO : edit 
  { id: "s2v_save_result_to_json", label: "Finalize Draft" }, // TODO : edit 
] as const;

export type DirectStepId = typeof STEPS_DIRECT[number]["id"];

export type ProgressStepStatus = "completed" | "running" | "failed" | "pending";

/**========================================================================
 * *                                PROJECT APIS
 *   
 *   - CRUD เกี่ยวกับ Project เช่น เรียกรายการ (List) / สร้าง / แก้ไข / ลบ
 *   
 *
 *========================================================================**/

export type CronConfig = {
  enabled: boolean;
  days: Days[];
  hour: number;
  minute: number;
};

/**
 * **API NAME**
 */
export type ScheduleConfig = Partial<{
  rss_urls: string[];
  cutoff_days: number;
  cron: CronConfig;
}>;

// | Record<string, never>

/**
 * **API NAME**: Project List Response
 * 
 * **METHOD**: GET
 * 
 * **API PATH**: *\/projects/*
 */
export type ProjectSerializer = {
  id: number;
  name: string;
  description: string;
  is_shared: boolean;
  role: "editor" | "viewer" | "owner"; // "owner" มาจาก POST /projects เท่านั้น (GET คืน editor/viewer) — เช็คสิทธิ์แก้ด้วย common/projectRole.ts
  youtube_creds: any | null; // ** something | null
  user_id: string;
  series: SeriesSerializer[];
};

/**
 * **API NAME**: Create Project
 * 
 * **METHOD**: POST
 * 
 * **API PATH**: *\/projects/*
 */
export type ProjectPayload = {
  name: string;
  description?: string;
};

/**
 * **API NAME**: Series List / Payload
 * 
 * **METHOD**: GET / POST
 * 
 * **API PATH**: *\/project/{project_id}/series/*
 */
export type SeriesSerializer = {
  id?: number;
  project_id: number;
  name: string;
  series_type: SeriesType;
  schedule_config: ScheduleConfig;
};

/**
 * **API NAME**: Create Run (Unified)
 * 
 * **METHOD**: POST
 * 
 * **API PATH**: *\/run/*
 */
export type CreateRunPayload = {
  project_id: number;
  series_id: number | null;
  type: SeriesType;
};


// Payload /run/${thread_id}/start ของ rss_news type
export type StartRssNewsPayload = {
  dry_run: boolean;
  manual_script: string | null;
  his_division?: number;
  cal_cost?: boolean;
};

// Response from Stop Run API
export type StopRunSerializer = {
  status: string;
  thread_id: string;
};

// Payload for Edit Generated Video
export type UpdateItemPayload = {
  item_id: number | string;
  headline?: string;
  script?: string;
  voice_guide?: string;
  voice_script?: string;
  image_prompt?: string;
};

// UpdateItem Response
export type UpdateItemSerializer = {
  regen_list: any[]; // todo ---
  status: string;
};

// Payload for Edit Youtube Metadata
export type UpdateMetadataPayload = {
  youtube_title: string;
  thumbnail_prompt: string;
  thumbnail_date?: string;
};

// Update Metadata Response
export type UpdateMetadataSerializer = {
  status: string;
};

// RunState Response
export type RunStateSerializer = {
  project_id: number;
  project_name: string;
  series_id: number;
  series_name: string;
  created_at: string;
  config: RunStateConfig;
  metadata: RunStateMetaData;
  next: Steps[];
  progress: RunStateProgress;
  tasks: string[];
  values: RunStateValue;
};

export type RunStateConfig = {
  configurable: {
    checkpoint_id: string;
    checkpoint_ns: string;
    thread_id: string;
  };
};

export type RunStateMetaData = {
  step: number;
  source: string;
  parent: any; // todo --- Meta Data Type
};

export type RunStateProgress = Partial<{
  load_data: ProgressCore;
  process_items: ProgressCore & {
    current: number;
    current_item: string;
    total: number;
    finished_at?: string;
  };
  generate_assets: ProgressCore & any; // todo ---
  merge_media: ProgressCore & any; // todo ---
  prepare_youtube_metadata: ProgressCore & any; // todo ---
  create_thumbnail: ProgressCore & any; // todo ---
  upload_to_youtube: ProgressCore & any; // todo ---
}>;

export type ProgressCore = {
  detail: string;
  logs: string[];
  status: string; // todo ---
  error?: string;
};

export type RunStateValue = {
  type: SeriesType;
  created_by: string;
  created_by_avatar: string | null;
  device_name: string;
  error: string;
  hostname: string;
  items: GeneratedVideoItem[];
  step_durations: StepDurations;
  // --- after generate done
  concat_list_path?: string;
  final_audio_path?: string;
  final_video_path?: string;
  thumbnail_path?: string;
  thumbnail_prompt?: string;
  thumbnail_date?: string;
  youtube_title?: string;
  youtube_description?: string;
};

export type GeneratedVideoItem = {
  id: string;
  headline: string | null;
  duration: number;
  audio_path: string;
  image_path: string;
  image_prompt: string;
  script: string;
  voice_guide?: string;
  voice_script?: string;
  voice_name: string;
  active?: boolean;
  seq?: number;
};

export type StepDurations = Partial<Record<Steps, number>>;

// Rerun Run Response
export type RerunRunSerializer = {
  thread_id: string;
  status: string;
};

export type DeleteRunSerializer = {
  id: string;
  status: string;
};

export type CompositionItem = {
  id: string;
  active: boolean;
  seq: number;
};

export type UpdateCompositionPayload = {
  items: CompositionItem[];
  render_mode: RenderMode;
};

export type DuplicateRunSerializer = {
  status: string;
  source_thread_id: string;
  new_thread_id: string;
};

/**========================================================================
 **                            STORY2SCENE TYPES
 *========================================================================**/

export type ScriptMode = "creative" | "direct";

// Direct Mode Item (มีเรื่องมาแล้ว)
export type DirectSceneItem = {
  id: string;
  script: string;
  voice_guide?: string;
  voice_script?: string;
  image_prompt: string;
  voice_name?: string;
};

// Creative Mode - Part Item (หลังจาก AI แตก Part)
export type StoryPartItem = {
  title: string;
  description: string;
  mood: string;
};

// Creative Mode - Script With Image Item (หลังจาก Generate Script)
export type ScriptWithImageItem = {
  id: string;
  headline: string;
  script: string;
  voice_guide?: string;
  voice_script?: string;
  voice_name: string; // ***
  image_prompt: string; // Final Image Prompt 
  scene_description: string;
  is_animated?: boolean;
  anim_prompt?: string;
  anim_mode?: "single" | "multi";
  is_uploaded?: boolean;
  use_animated?: boolean;
  uploaded_video_url?: string;
  external_video_link?: string | null; // หลังบ้านส่งมาอยู่แล้ว — ฉากที่ใช้คลิปจากลิงก์ภายนอก (MeLab); เป็น protected field ไม่ต้องส่งกลับ
  is_image_uploaded?: boolean;
  uploaded_image_url?: string;
  text_overlays?: TextOverlay[];
  image_overlays?: ImageOverlay[];
  image_model?: string;
  reference_images?: string[]; // filename[] ของ ImageAsset
  image_style?: string;
  image_style_prompt?: string;
  ken_burns_direction?: string;
  character_id?: number;
  active?: boolean;
  video_model?: string;
  padding_start?: number; // seconds of silence before this scene's speech (0-10)
  padding_end?: number; // seconds of silence after this scene's speech (0-10)
};

// Final Script (Result สุดท้ายก่อนสร้าง Video)
export type FinalScriptData = {
  generated_at: string; // ISO datetime format
  title_podcast: string;
  type: string;
  items: ScriptWithImageItem[];
  thumbnail_prompt?: string;
  is_uploaded?: boolean;
  uploaded_video_url?: string;
  is_image_uploaded?: boolean;
  uploaded_image_url?: string;
  audio_only?: boolean;
};

// Story Input (สำหรับส่งไปให้ AI แตก Part)
export type StoryInput = {
  title: string;
  description: string;
};

// Full Parts Input (สำหรับส่งไป Generate Script)
export type FullPartsInput = {
  title: string;
  items: StoryPartItem[];
};

// Direct Script Input (มีเริ่องมาแล้ว - ส่งตรงไปยังสร้าง Video)
export type DirectScriptInput = {
  title: string;
  image_style?: string;
  image_style_prompt?: string;
  aspect_ratio?: string;
  voice_name?: string;
  audio_only?: boolean;
  items: {
    id: string;
    script: string;
    voice_guide?: string;
    voice_script?: string;
    visual: string;
  }[];
};

// Story2Scene GraphState Values
export type Story2SceneValues = Partial<{
  story: StoryInput;
  full_parts: { items: StoryPartItem[] };
  final_script: FinalScriptData;
  items: GeneratedVideoItem[];
  thumbnail_path: string;
  final_video_path: string;
  step_durations?: Record<string, number>; // todo --- Story2SceneSteps type แทน string
}>;

// Story2Scene Progress
// todo --- Story2SceneSteps type แทน string
export type Story2SceneProgress = Record<string, {
  status: string;
  detail?: string;
  generated_assets?: Record<string, { audio: string | null; image: string | null; video: string | null }>;
  logs?: string[];
  current?: number;
  current_item?: string;
  duration?: number;
  started_at?: string;
  updated_at?: string;
  finished_at?: string;
  total?: number;
  error?: string;
}>;

// Story2Scene GraphState
export type Story2SceneGraphState = {
  values: Story2SceneValues;
  next: string[];
  created_at: string;
  progress: Story2SceneProgress;
};

// Payload for Start Story (Creative Mode - Step 1)
export type StartStoryPayload = {
  story: StoryInput;
  force_regen?: boolean;
};

// Payload for Submit Parts (Creative Mode - Step 2)
export type SubmitPartsPayload = {
  full_parts: FullPartsInput;
  force_regen?: boolean;
};

// Payload for Generate Video from Script (Creative Mode - Step 3)
export type GenerateVideoFromScriptPayload = {
  dry_run: boolean;
  manual_script: FinalScriptData;
};

// Payload for Direct Generate Video (Direct Mode)
export type DirectGeneratePayload = {
  dry_run: boolean;
  manual_script: null;
  script2vdo: DirectScriptInput;
};

/**========================================================================
 **                            SCRIPT2VDO TYPES
 *========================================================================**/

export type DirectScriptGraphState = {
  values: DirectScriptValues;
  next: string[];
  created_at: string;
  project_id?: number;
  series_id?: number;
  render_mode?: string;
  audio_only?: boolean;
  progress: Record<string, DirectScriptProgress>;
};

export type DirectScriptProgress = {
  status: ProgressStepStatus;
  skipped: boolean;
  step_index: number;
  total_steps: number;
  // existing fields
  started_at?: string;
  updated_at?: string;
  finished_at?: string;
  duration?: number;
  detail?: string | null;
  error?: string | null;
  logs?: string[];
  current?: number;
  total?: number;
  current_item?: string;
  generated_assets?: Record<string, { audio: string; image: string; video: string }>;
};

export type DirectScriptValues = Partial<{
  raw_scripts_images: {
    title_podcast: string;
    title?: string; // edit case
    items: { id: string; script: string; voice_guide?: string; voice_script?: string; visual: string }[];
  };
  final_script: FinalScriptData;
  items: DirectScriptValueItem[];
  thumbnail_path: string;
  final_video_path: string;
  scripts_with_images?: ScriptWithImageItem[];
  step_durations?: Record<string, number>;
  error?: string;
  audio_only?: boolean;
  youtube_title?: string;
  padding_start?: number; // run-level default (seconds) — used by scenes without their own padding
  padding_end?: number;
}>;

export type DirectScriptValueItem = {
  id: string;
  image_prompt: string;
  scene_description: string;
  script: string;
  voice_guide?: string;
  voice_script?: string;
  anim_prompt?: string;
  is_animated?: boolean;
  audio_path?: string;
  image_path?: string;
  video_path?: string;
  headline: string;
  duration?: number;
  voice_name?: string;
  anim_mode?: "single" | "multi";
  image_style?: string;
  image_style_prompt?: string;
  aspect_ratio?: string;
};

export type DirectScriptOutputItem = {
  id: string;
  script: string;
  voice_guide?: string;
  voice_script?: string;
  image_prompt: string;
  audio_url: string;
  image_url: string;
  ai_video_url: string | null;
  uploaded_video_url: string | null;
  external_video_link?: string | null; // หลังบ้านส่งมาอยู่แล้ว — ฉากที่ใช้คลิปจากลิงก์ภายนอก (MeLab); เป็น protected field ไม่ต้องส่งกลับ
  segment_video_url: string | null;
  uploaded_image_url: string | null;
  active?: boolean; // หลังบ้านส่งมาอยู่แล้ว — ฉากที่ปิดจะไม่ถูกนำไปเรนเดอร์
  seq?: number; // หลังบ้านส่งมาอยู่แล้ว — ลำดับฉากตอนเรนเดอร์ (index+1 ของ script)
};

export type DirectScriptOutputResponse = {
  render_mode: "review" | "final";
  final_video_url: string | null;
  items: DirectScriptOutputItem[];
  audio_only?: boolean; // หลังบ้านส่งมาอยู่แล้ว — run นี้สร้างเฉพาะเสียง (final_video_url เป็น .mp3)
  title?: string; // หลังบ้านส่งมาอยู่แล้ว — ชื่อตอนของ run
};

export type StartDirectScriptPayload = {
  script2vdo: DirectScriptInput;
  force: boolean;
};

export type StartGenerateDirectScriptPayload = {
  force_regen?: Record<string, string[]>;
  aspect_ratio?: string;
  manual_script: FinalScriptData;
  his_division?: number;
  cal_cost?: boolean;
  generate_item_id?: string;
  actions?: string[]; // ["gen", "merge"]
  audio_only?: boolean;
  padding_start?: number; // run-level default silence before speech (0-10 s); per-scene item value overrides
  padding_end?: number; // run-level default silence after speech (0-10 s); per-scene item value overrides
};

export type TextOverlayStyle = {
  font?: string; // *** ยึดตาม Font ที่ Supported
  fontSize?: number;
  color?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  alignment?: string;
  outlineColor?: string;
  shadowColor?: string;
  animation?: "none" | "pop-in" | "fade" | "slide-left" | "slide-right" | "slide-top" | "slide-bottom";
};

export type TextOverlay = {
  id: string;
  text: string;
  style: TextOverlayStyle;
  start?: number;
  end?: number;
};

export type ImageOverlayPosition =
  | "top-left"
  | "top-right"
  | "bottom-left"
  | "bottom-right"
  | "center";

export type ImageOverlayAnimation =
  | "none"
  | "fade"
  | "slide-up"
  | "slide-down"
  | "slide-left"
  | "slide-right";

export type ImageOverlay = {
  id: string;
  url: string;
  position: ImageOverlayPosition;
  animation: ImageOverlayAnimation;
  start: number;
  duration: number;
};

// Image Asset (thread-scoped) — ใช้เป็น reference image สำหรับ Image Editing models
export type ImageAsset = {
  created_at?: number;
  filename: string; // ใช้ส่งใน reference_images
  original_name: string;
  url: string; // ex. "/static/<thread_id>/thread_asset_....png"
};

export type MediaMeta = {
  script?: string;
  voice?: string;
  updated_at?: string;
};

export type MediaVersion = {
  version: number;
  path: string;
  signature: string;
  created_at?: string;
  meta?: MediaMeta;
};

export type SegmentVersionResponse = {
  segment_id: string;
  asset_type: AssetType;
  current: number | null; // null = ยังไม่มี version (หลังบ้านคืน None เมื่อ segment มีแต่ asset ชนิดนี้ยังไม่มี)
  versions: MediaVersion[];
};

export type SetCurrentVersionPayload = {
  version: number;
};

export type SetCurrentVersionResponse = {
  status: string;
  thread_id: string;
};

export type RegenerateItem = {
  id: string;
  audio: boolean;
  image: boolean;
  video: boolean;
};

export type CostDetails = {
  can_use: boolean;
  message?: string;
  default_per_scene_credit?: string;
  video_per_scene_credit?: string;
  total_default_scene_credit?: string;
  total_video_scene_credit?: string;
  total_scene_credit: string;
  previous_wallet_balance?: string;
  current_wallet_balance?: string;
};

export type CheckCostResponse = {
  status: string;
  thread_id: string;
  regenerate_items: RegenerateItem[];
  cost_details: CostDetails;
  error: string;
};

export type UpdateTitlePayload = {
  title: string;
};

export type UpdateTitleResponse = {
  status: string;
  thread_id: string;
  title: string;
};

/**========================================================================
 **                            SCRIPT2SCENE TYPES
 *========================================================================**/

export type StartScriptToSceneSerializer = {
  status: string;
  thread_id: string;
};

// Item ใน raw_scripts_images
export type ScriptToSceneItem = {
  id: string;
  script: string;
  voice_guide?: string;
  voice_script?: string;
  visual: string;
};

// raw_scripts_images ใน values
export type ScriptToSceneRawScriptImages = {
  title_podcast: string;
  items: ScriptToSceneItem[];
};

// Progress ของแต่ละ Step
export type ScriptToSceneProgressStep = {
  status: string;
  started_at?: string;
  updated_at?: string;
  finished_at?: string;
  duration?: number;
  detail?: string | null;
  logs?: string[];
  raw_scripts_images?: ScriptToSceneRawScriptImages;
  error?: string;
};

// Value ของ Graph State
export type ScriptToSceneValues = {
  run_dir: string;
  script_text: string;
  raw_scripts_images: ScriptToSceneRawScriptImages;
  force_regen: boolean;
  step_durations: Record<string, number>;
};

// Graph State
export type ScriptToSceneGraphState = {
  values: ScriptToSceneValues;
  next: string[];
  created_at: string;
  progress: Record<string, ScriptToSceneProgressStep>;
};

// Payload สำหรับ Start
export type StartScriptToScenePayload = {
  script: string;
};

export type RefineScriptToSceneItem = {
  id: string;
  script: string;
  voice_guide?: string;
  voice_script?: string;
  visual: string;
};

export type RefineScriptToSceneCurrentData = {
  title_podcast: string;
  items: RefineScriptToSceneItem[];
};

export type RefineScriptToScenePayload = {
  current_data: RefineScriptToSceneCurrentData;
  prompt: string;
};

// Response มี structure เหมือน ScriptToSceneGraphState
export type RefineScriptToSceneResponse = {
  status: string;
  values?: {
    raw_scripts_images?: ScriptToSceneRawScriptImages;
  };
};

/**========================================================================
 **                            MOVE FROM MEGEN INTEFACE
 *========================================================================**/

export type UserProfile = {
  first_name: string;
  last_name: string;
  email: string;
  image?: string;
} | null;

// todo --- Update GraphState Type
export type GraphState = {
  thread_id: string;
  created_at?: string;
  next: string[];
  values: any;
  progress?: Record<string, { current: number; total: number; detail?: string; status?: string; error?: string }>;
};

export type RunData = {
  id: string;
  title: string;
  video_id?: string;
  created_at?: string;
  status: string;
  created_by?: string;
  created_by_avatar?: string;
  device_name?: string;
  hostname?: string;
  error?: string;
  run_type?: SeriesType;
  series_id: number | null;
  project_id: number;
  /**
   * ฟิลด์ที่คำค้น (`q`) ไปตรง เรียงจากตรงที่สุด · `[]` เมื่อไม่ได้ส่ง `q`
   * optional เพราะฝั่งเทสต์สร้าง RunData เองจาก Partial (ดู runsQuery.test.ts)
   */
  matched_on?: ("title" | "video_id" | "thread_id")[];
};

export type RunResponse = {
  thread_id: string;
};

export type RunsResponse = {
  runs: RunData[];
  /** จำนวนทั้งหมดหลังกรอง permission + q + status — ไม่ใช่จำนวนใน `runs` */
  total: number;
  /** limit ที่ server ใช้จริงหลัง clamp — อ่านค่านี้ ไม่ใช่ค่าที่ส่งไป */
  limit: number;
  offset: number;
  has_more: boolean;
};

/**========================================================================
 **                            SHARE PROJECT TYPES
 *========================================================================**/

export type ShareRole = "viewer" | "editor";

export type ShareResource = "project" | "series" | "runmap";

// -- เป็นทั้ง Payload / Response จาก METHOD GET
export type ShareMemberSerializer = {
  user_id: string;
  role: ShareRole;
};

/**
 * **PATH**: /share
 * 
 * **METHOD**: GET
 */
export type ShareProjectParams = {
  resource_type: ShareResource;
  project_id: number;
  series_id?: number;
  thread_id?: string;
};

/**
 * **PATH**: /share
 * 
 * **METHOD**: POST
 */
export type ShareProjectPayload = {
  resource_type: ShareResource;
  project_id: number;
  series_id: number | null;
  thread_id: string | null;
  users: ShareMemberSerializer[];
};

/**
 * **PATH**: /share
 * 
 * **METHOD**: DELETE
 */
export type DeleteSharedMemberPayload = {
  resource_type: ShareResource;
  project_id: number;
  series_id: number | null;
  thread_id: string | null;
  user_ids: string[];
};

export type SearchOaUserResponse = {
  items: ProfiledDivisionHasUserSerializer[];
  total: number;
  next: string | null;
  previous: string | null;
};

export type ShareRunMapData = {
  id: string;
  project_id: number;
  series_id: number | null;
  name: string;
};

/**========================================================================
 **                            CONNECTION TYPES
 *========================================================================**/

// todo - real response from youtube auth
export type YouTubeAuthResp = {
  authorization_url: string;
};

export type YouTubeChannel = {
  _id: number;
  id?: string;
  status: "connected" | "expired";
  channel_name: string | null;
  expiry: string | null;
};

// // เดิม (Single Channel)
// export type YouTubeStatusResp = {
//   status: "connected" | "required_youtube"; // ? string -- กรณีมี status เพิ่มเติม
//   name?: string;
//   expiry?: string;
// };

export type YouTubeStatusResp = {
  status: "connected" | "required_youtube";
  channels: YouTubeChannel[];
  name?: string;
  expiry?: string;
};

/**========================================================================
 **                            CHARACTER TYPES
 *========================================================================**/

export type CharacterSerializer = {
  id: number;
  series_id: number;
  name: string;
  description: string;
  style: string; // ***
  image_url: string | null;
  is_validated: boolean;
  validation_message: string | null;
  created_at: string;
};

export type GenerateCharacterPayload = {
  series_id: number;
  name: string;
  prompt: string;
  style: string;
};

export type UploadCharacterPayload = {
  series_id: number;
  name: string;
  file: File;
  description?: string;
};

export type DeleteCharacterResponse = {
  status: string;
};

/**========================================================================
 **                               MEDIA TYPES
 *========================================================================**/

export type MediaStyle = {
  prompt: string;
  text: string;
  value: string;
};

export type VoiceSerializer = {
  name: string;
  gender: "male" | "female" | "neutral";
  age: string;
  tone: string;
  pitch: string;
  speed: string;
  use_cases: string[];
};

// ── Image Model Catalog (GET /media/image-models) — หลังบ้านเป็นเจ้าของ list/default/ความสามารถ ──
export type ImageModelMode = "t2i" | "edit" | "ref";

export type ImageModel = {
  id: string;
  label: string;
  family: string;
  modes: ImageModelMode[]; // t2i = ไม่มีรูป input, edit = 1 รูป, ref = หลายรูป
  max_input_images: number; // เพดานรูปอ้างอิงต่อ 1 call (0 = t2i อย่างเดียว)
  enabled: boolean; // false = ซ่อนจาก dropdown (thread เก่าที่ stamp ไว้ยัง render ได้)
};

export type ImageModelCatalog = {
  default: string; // โมเดลที่หลังบ้านใช้เมื่อไม่ระบุ — ห้าม hardcode ในหน้าบ้าน
  modes: Record<ImageModelMode, string>; // คำอธิบายต่อโหมด (ไม่ใช่ id)
  models: ImageModel[];
};

// ── Video Model Catalog (GET /media/video-models) — หลังบ้านเป็นเจ้าของ list/default/alias/ความสามารถ ──
export type VideoModelKind = "i2v" | "r2v" | "lipsync";

export type VideoModel = {
  id: string;
  label: string;
  family: string;
  provider: string; // dashscope | google | comfyui
  kind: VideoModelKind;
  max_duration_seconds: number | null; // หลังบ้าน clamp ให้เอง (null = ไม่มีเพดาน)
  generates_audio: boolean; // หลังบ้านใช้ตัดสิน payload — หน้าบ้านไม่ต้องโชว์
  supports_character: boolean; // false = เลือก character ไปก็ถูกทิ้งเงียบ ๆ
  requires_anim_prompt: boolean; // false = ใช้ได้โดยไม่ต้องมี Video Prompt (lip-sync)
  enabled: boolean; // false = ซ่อนจาก dropdown (thread เก่าที่ stamp ไว้ยัง render ได้)
};

export type VideoModelCatalog = {
  default: string; // โมเดลที่หลังบ้านใช้เมื่อไม่ระบุ — ห้าม hardcode ในหน้าบ้าน
  kinds: Record<VideoModelKind, string>; // คำอธิบายต่อ kind (ไม่ใช่ id)
  aliases: Record<string, string>; // id เก่า → id จริง ใช้ "แสดงผล" เท่านั้น ห้ามเขียนกลับลง item
  models: VideoModel[];
};
/**========================================================================
 **                              AI USAGE TYPES
 *========================================================================**/

// AI API rows plus source-qualified FFmpeg task rows used by the cost dashboard.
export type AiUsageRecordRow = {
  id: number | string;
  ffmpeg_task?: FfmpegUsageTask;
  thread_id: string | null;
  user_id: string | null;
  user_name?: string | null; // ชื่อจริงจาก user_directory ฝั่ง backend (null = ยังไม่รู้จัก id → แสดง user_id)
  service: string; // image | video | tts | llm
  provider: string; // dashscope | google | gemini | comfyui | local | ...
  model: string;
  caller: string | null;
  status: string; // "success" | "error" | ...
  duration_ms: number | null;
  input_tokens: number;
  output_tokens: number;
  input_characters: number;
  audio_duration: number; // seconds
  video_duration: number; // seconds
  requested_duration: number | null;
  resolution: string | null;
  audio_enabled?: boolean | null; // มิติคิดเงิน video (wan แยกราคาเปิด/ปิดเสียง)
  input_video_seconds?: number | null; // R2V คิดความยาว reference video ที่ส่งเข้า
  image_count: number;
  created_at: string | null; // ISO
  error_message: string | null;
};

export type AiUsageRecordsResponse = {
  records: AiUsageRecordRow[];
  total: number;
  limit: number;
  offset: number;
};

export type FfmpegUsageTask = {
  id: string;
  status: string | null;
  task_type: string | null;
  user_id: string | null;
  user_name?: string | null;
  thread_id: string | null;
  output_key: string;
  created_at: string | null;
  error_message: string | null;
  active_seconds: number | null;
  gpu_compute_seconds: number | null;
  allocated_instance_seconds: number | null;
  allocated_cost: number | null;
  worker_run_id: string | null;
  ffmpeg_execution?: {
    version: 1;
    mode: "nvenc" | "stream_copy" | "software" | "other";
    video_encoder: string | null;
    exit_code: number;
    output_bytes: number;
    gpu_encoding_verified: boolean;
  } | null;
  worker_run: {
    id: string;
    gpu_name: string | null;
    hourly_rate: number | null;
    rate_currency: string | null;
  } | null;
};

export type FfmpegUsageResponse = {
  total: number;
  items: FfmpegUsageTask[];
  limit: number;
  offset: number;
};

// GET /ai-usage/thread/{thread_id}
export type AiUsageTotals = {
  calls: number;
  images: number | null;
  video_seconds: number | null;
  input_video_seconds?: number | null;
  audio_seconds: number | null;
  input_tokens: number | null;
  output_tokens: number | null;
  duration_ms?: number | null;
  errors: number;
};

export type AiUsageByModelRow = AiUsageTotals & {
  service: string;
  provider: string;
  model: string;
  // /thread groups one row per billing variant when its timeline is truncated.
  resolution?: string | null;
  audio_enabled?: boolean | null;
  // NULL is a legacy success row; any non-success value is not billable for cloud providers.
  status?: string | null;
};

export type AiUsageTimelineRow = {
  created_at: string;
  service: string;
  provider: string;
  model: string;
  user_id: string | null;
  caller: string | null;
  image_count: number | null;
  video_duration: number | null;
  requested_duration: number | null;
  audio_duration: number | null;
  input_tokens: number | null;
  output_tokens: number | null;
  resolution: string | null;
  audio_enabled?: boolean | null; // มิติคิดเงิน video (wan แยกราคาเปิด/ปิดเสียง)
  input_video_seconds?: number | null; // R2V: วินาที reference video ที่ถูกบิล (backend คิด billable ให้แล้ว)
  duration_ms: number | null;
  status: string;
};

export type AiUsageThreadDetail = {
  ffmpeg_tasks?: FfmpegUsageTask[]; // UI joins the separately paged task endpoint after run permission succeeds.
  thread_id: string;
  users: string[];
  user_names?: Record<string, string>; // id→ชื่อ จาก backend (id ที่ยังไม่รู้จักไม่มี key)
  totals: AiUsageTotals;
  by_model: AiUsageByModelRow[];
  timeline: AiUsageTimelineRow[];
};
